import { useSessionApp } from '../../../../utils/actionableNotifications';
import { CustomerReferenceEditor } from '../queries/CustomerReference';
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Drawer, Spin, Tag, Tabs, Badge, Button, Empty, Space, Typography, theme } from "antd";
import { Select } from "../../../shared/OptionWidthControl";
import {
  UserOutlined, ShoppingOutlined, DollarOutlined, SendOutlined,
  StopOutlined, RollbackOutlined, NotificationOutlined,
  MessageOutlined, CloseOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../../contexts/AuthContext";
import { useSocket } from "../../../contexts/SocketContext";
import { isMoveBackAvailable } from "../modals-misc/MoveStatusModal";
import { sameEntityId, URGENCY_CFG, queryStatusLabel } from "../../pipeline";
import { useTaskDetail } from "../../viewmodels/shared/useTaskDetail";
import { useAgentsUsers } from "../../viewmodels/shared/useAgentsUsers";
import { OverviewTab } from "./OverviewTab";
import { PartsTab } from "./PartsTab";
import { PaymentHandoverTab } from "./PaymentHandoverTab";
import { SentSuppliersTab } from "./SentSuppliersTab";
import { UnavailablePartsModal } from "../modals-misc/UnavailablePartsModal";
import { BillingDetailsModal } from "../modals-misc/BillingDetailsModal";
import { SendToSuppliersModal } from "../../../../features/supplier-enquiries/views/SendToSuppliersModal";
import { AutomationPanel } from '../../../../features/supplier-enquiries/views/AutomationPanel';
import { PriceUpdateDrawer } from "../price-drawer/PriceUpdateDrawer";
import { useAccess } from "../../../../features/access/context/AccessProvider";
import { fetchCommunicationChannels, updateQueryCommunicationChannel } from "../../models/shared/communicationChannelsModel";
import { channelKey, channelLabel } from "../shared/CommunicationChannelPicker";
import "./workflow-task-drawer.css";
import ScrollEdgeFrame from "../../../shared/ScrollEdgeFrame";
import { selectedPartQuote } from "../../models/price-drawer/quoteEditingPolicy";
import { usePaymentsV2Query } from '../../../payments-v2/viewmodels/usePaymentsV2Query';

const visibleInitialTab = tab => ["history", "customer-messages"].includes(tab) ? "overview" : tab || "overview";

export function WorkflowTaskDrawer({ queryPk, initialTab = "overview", onClose, onSent, onReassignClick, onUnparkClick, onMoveBackClick, onCancelClick, onBillingDetailsSent, onSendModalOpenChange, onOpenPaymentModal }) {
  const { message } = useSessionApp();
  const { token } = theme.useToken();
  const { authState } = useContext(AuthContext);
  const { can } = useAccess();
  const paymentAuthority = usePaymentsV2Query(queryPk, { enabled: !!queryPk && can('payments.view') });
  const canEditQuery = can("sales_pipeline.edit_query");
  const canManageParts = can("sales_pipeline.manage_parts");
  const canManageSupplierQuotes = can("sales_pipeline.manage_supplier_quotes");
  const canSendSupplierRequests = can("sales_pipeline.send_supplier_requests");
  const canSendCustomerMessages = can("sales_pipeline.send_customer_messages");
  const canChangeQueryStatus = can("sales_pipeline.change_status");
  const { socket: mainSocket } = useSocket();
  const { data, loading, error, refetch } = useTaskDetail(queryPk);
  const { users } = useAgentsUsers();
  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u.fullName])), [users]);


  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [unavailableModalOpen, setUnavailableModalOpen] = useState(false);
  const [billingDetailsModalOpen, setBillingDetailsModalOpen] = useState(false);
  const [priceDrawerOpen, setPriceDrawerOpen] = useState(false);
  const [priceDrawerCard, setPriceDrawerCard] = useState(null);
  const [activeTab, setActiveTab] = useState(visibleInitialTab(initialTab));
  const [communicationChannels, setCommunicationChannels] = useState([]);
  const [channelSaving, setChannelSaving] = useState(false);

  useEffect(() => {
    if (priceDrawerOpen && (!queryPk || !sameEntityId(priceDrawerCard?.id, queryPk))) {
      setPriceDrawerOpen(false);
    }
  }, [priceDrawerOpen, priceDrawerCard?.id, queryPk]);

  useEffect(() => { if (queryPk) setActiveTab(visibleInitialTab(initialTab)); }, [queryPk, initialTab]);
  useEffect(() => {
    if (!queryPk) return;
    fetchCommunicationChannels().then(setCommunicationChannels).catch(() => {});
  }, [queryPk]);

  useEffect(() => {
    onSendModalOpenChange?.(sendModalOpen);
  }, [sendModalOpen, onSendModalOpenChange]);

  const queryPkRef = useRef(queryPk);
  useEffect(() => { queryPkRef.current = queryPk; }, [queryPk]);
  const priceDrawerOpenRef = useRef(priceDrawerOpen);
  useEffect(() => { priceDrawerOpenRef.current = priceDrawerOpen; }, [priceDrawerOpen]);

  // Silently refresh when quotes, parts, or task-level updates arrive
  useEffect(() => {
    if (!mainSocket) return;
    const handle = (evtData) => {
      const pk = evtData?.query_pk ?? evtData?.queryPk ?? evtData?.data?.task_id ?? evtData?.data?.query_pk ?? evtData?.data?.payment?.query_pk;
      // PriceUpdateDrawer owns quote/part updates while it is open and patches
      // them locally from the same socket events. Refreshing its parent at the
      // same time creates competing reads during the mutation transaction and
      // was the trigger for the recurring blank-drawer regression. The close
      // handler performs one authoritative refresh after the nested workflow is
      // finished.
      if (pk && sameEntityId(pk, queryPkRef.current) && !priceDrawerOpenRef.current) refetch();
    };
    mainSocket.on("query:quotes_updated", handle);
    mainSocket.on("query:parts_updated", handle);
    mainSocket.on("query:updated", handle);
    mainSocket.on("query:payment_manually_received", handle);
    mainSocket.on("query:payment_updated", handle);
    mainSocket.on("query:after_sales_updated", handle);
    return () => {
      mainSocket.off("query:quotes_updated", handle);
      mainSocket.off("query:parts_updated", handle);
      mainSocket.off("query:updated", handle);
      mainSocket.off("query:payment_manually_received", handle);
      mainSocket.off("query:payment_updated", handle);
      mainSocket.off("query:after_sales_updated", handle);
    };
  }, [mainSocket, refetch]);

  const task         = data?.task         ?? null;
  const parts        = data?.parts        ?? [];
  const unavailableParts = parts.filter(part => part.status !== "disabled" && !selectedPartQuote(part));
  const afterSales   = data?.after_sales  ?? null;
  const movements    = data?.movements    ?? [];
  const urgCfg       = task?.urgency ? URGENCY_CFG[task.urgency] : null;
  const communication = task?.customer_communication_channel || null;

  const openPriceDrawer = () => {
    if (!task || !queryPk) return;
    // Keep the child drawer anchored to an immutable query identity. A task
    // detail/socket refresh may replace or temporarily omit presentation
    // fields, but it must never turn an already-open price drawer into a
    // card-less drawer.
    setPriceDrawerCard({
      id: queryPk,
      query_id: task.query_id,
      status: task.status,
      customer: task.customer,
      vehicle: task.vehicle,
    });
    setPriceDrawerOpen(true);
  };

  const changeCommunicationChannel = async nextKey => {
    if (!queryPk || !nextKey || nextKey === channelKey(communication)) return;
    setChannelSaving(true);
    try {
      await updateQueryCommunicationChannel(queryPk, {
        communication_channel_key: nextKey,
        ...(communication?.version ? { expected_version: communication.version } : {}),
      });
      message.success("Customer communication channel updated");
      await refetch();
    } catch (error) {
      if (error?.response?.status === 409) {
        await refetch();
        message.warning(error?.response?.data?.message || "The query changed. The latest channel has been loaded.");
      } else message.error(error?.response?.data?.message || error.message || "Could not update the communication channel");
    } finally { setChannelSaving(false); }
  };

  const sendAttempts = Array.isArray(task?.supplier_send_info) ? task.supplier_send_info : [];
  const initialGroups = [...new Set(sendAttempts.flatMap(a => a.selected_groups ?? []))];
  const previouslyContactedIds = new Set(sendAttempts.flatMap(a => (a.suppliers ?? []).map(s => s.supplier_id)));

  const taskActions = [
    canSendCustomerMessages && task?.status !== "draft" && unavailableParts.length > 0 && {
      key: "unavailable", label: "Notify unavailable", icon: <NotificationOutlined />,
      onClick: () => setUnavailableModalOpen(true),
    },
    onCancelClick && canChangeQueryStatus && !["cancelled", "completed", "disabled"].includes(task?.status) && {
      key: "lost", label: "Mark lost", icon: <StopOutlined />, danger: true,
      disabled: !paymentAuthority.capability('can_cancel_query').allowed,
      title: paymentAuthority.capability('can_cancel_query').reason || paymentAuthority.capability('can_cancel_query').reason_code || 'Not available',
      onClick: () => onCancelClick(queryPk, task?.query_id),
    },
  ].filter(Boolean);

  const tabs = [
    {
      key: "overview",
      label: "Overview",
      icon: <UserOutlined />,
      children: task ? <OverviewTab task={task} afterSales={afterSales} parts={parts} userMap={userMap} movements={movements}
        onOpenCustomerProfile={onClose}
        onReassign={onReassignClick && can("sales_pipeline.reassign_query") ? () => onReassignClick(queryPk) : undefined}
        onShowParts={() => setActiveTab("parts")}
        taskActions={taskActions.map(action => <Button key={action.key} size="small" aria-label={action.label} icon={action.icon} danger={action.danger} onClick={action.onClick}>{action.label}</Button>)}
        communicationControls={<div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "nowrap" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap", flexShrink: 0 }}>
              <label htmlFor={`query-channel-${queryPk}`}>Customer messaging channel</label>
            </Typography.Text>
            <Select id={`query-channel-${queryPk}`} aria-label="Customer messaging channel" size="small"
              className="task-overview-channel-select" popupMatchSelectWidth={180}
              style={{ width: 180, minWidth: 0, flexShrink: 0, marginLeft: "auto" }} value={channelKey(communication)}
              loading={channelSaving} disabled={!canEditQuery || channelSaving} onChange={changeCommunicationChannel}
              options={communicationChannels.some(item => item.key === channelKey(communication)) || !channelKey(communication)
                ? communicationChannels.map(item => ({ value: item.key, label: item.label }))
                : [{ value: channelKey(communication), label: channelLabel(communication) }, ...communicationChannels.map(item => ({ value: item.key, label: item.label }))]} />
          </div>
          {communication?.compatibility_fallback && <Alert style={{ marginTop: 10 }} showIcon type="warning" message="WATI channel used temporarily" description="This older query has no explicit channel selection. WATI is being used temporarily and is not proof of its historical communication method." />}
        </div>}
      /> : null,
    },
    {
      key: "sent",
      label: (
        <Badge count={sendAttempts.length} size="small" offset={[6, 0]} color="#fa8c16">
          <span style={{ paddingRight: 6 }}>Sent</span>
        </Badge>
      ),
      icon: <SendOutlined />,
      children: <><AutomationPanel queryPk={queryPk} parts={parts} /><SentSuppliersTab attempts={sendAttempts} parts={parts} userMap={userMap} onSendMore={canSendSupplierRequests ? () => setSendModalOpen(true) : null} /></>,
    },
    {
      key: "parts",
      label: (
        <Badge count={parts.filter(p => p.status !== "disabled").length} size="small" offset={[6, 0]} color="var(--app-color-accent)">
          <span style={{ paddingRight: 6 }}>Parts</span>
        </Badge>
      ),
      icon: <ShoppingOutlined />,
      children: <PartsTab
        parts={parts} images={task?.images || []} queryPk={queryPk} userId={authState?.id}
        customerPhone={task?.customer?.customer_mobile}
        onPartPatched={refetch}
        onManagePrices={canManageSupplierQuotes ? openPriceDrawer : null}
        canManageParts={canManageParts}
        canEditQuery={canEditQuery}
      />,
    },
    {
      key: "payments",
      label: (
        <span>Payment &amp; Handover</span>
      ),
      icon: <DollarOutlined />,
      children: <PaymentHandoverTab
        key={queryPk}
        paymentVisible={can("payments.view")}
        paymentProps={{
          onOpenPaymentModal: () => onOpenPaymentModal?.({ id: queryPk, query_id: task?.query_id, status: task?.status, customer: task?.customer }),
        }}
        handoverProps={{ queryPk, customer: task?.customer, priority: task?.urgency || 1, canSubmit: can("sales_pipeline.handover_to_operations") }}
      />,
    },
    {
      key: "customer-messages",
      label: "Customer Messages",
      icon: <MessageOutlined />,
      children: <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span><strong>Customer message history is coming soon.</strong><br />Messages generated by completed business actions will appear here for viewing and copying.</span>} />
      </Space>,
    },

  ].filter(tab => tab.key !== "customer-messages");

  return (
    <>
    <Drawer
      open={!!queryPk}
      onClose={onClose}
      width={720}
      push={false}
      closable={false}
      extra={<Space size={4}>
        <Button type="text" aria-label="Close task drawer" icon={<CloseOutlined />} onClick={onClose} />
      </Space>}
      destroyOnHidden
      title={
        task ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700 }}>#{task.query_id}</span><CustomerReferenceEditor key={queryPk} queryPk={queryPk} value={task.customer_reference} status={task.status} allowed={canEditQuery} onSaved={refetch} />
            <Tag color="blue" style={{ margin: 0 }}>{queryStatusLabel(task.status || task.status_name)}</Tag>
            {urgCfg && (
              <Tag style={{ margin: 0, color: urgCfg.color, borderColor: urgCfg.color, background: urgCfg.pill }}>
                {urgCfg.label}
              </Tag>
            )}
            {canChangeQueryStatus && ((onUnparkClick && ["parked", "on_hold", "postponed"].includes(task?.status)) || (onMoveBackClick && isMoveBackAvailable(task?.status))) && <div style={{ display: "flex", gap: 6 }}>
              {onUnparkClick && canChangeQueryStatus && ["parked", "on_hold", "postponed"].includes(task?.status) && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onUnparkClick(queryPk)}>
                  Unpark
                </Button>
              )}
              {onMoveBackClick && canChangeQueryStatus && paymentAuthority.capability('can_move_back').allowed && isMoveBackAvailable(task?.status) && (
                <Button size="small" icon={<RollbackOutlined />} onClick={() => onMoveBackClick(queryPk, task?.query_id, task?.status)}>
                  Move Back
                </Button>
              )}
            </div>}
          </div>
        ) : "Task"
      }
      styles={{ body: { padding: "0 20px 20px", overflow: "hidden", display: "flex", flexDirection: "column" } }}
    >
      <ScrollEdgeFrame style={{ flex: 1, overflow: "hidden" }} topEdgeSelector=".workflow-task-tabs > .ant-tabs-nav">
      <div data-scroll-edge-viewport style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {loading && !task && (
        <div role="status" aria-label="Loading task" style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !task && queryPk && (
        error ? (
          <Alert
            showIcon
            type={error?.response?.status === 403 ? "warning" : "error"}
            message={error?.response?.status === 403 ? "You do not have access to this task" : "Could not load this task"}
            description={error?.response?.data?.message || error.message}
            action={<Button size="small" onClick={refetch}>Try again</Button>}
            style={{ marginTop: 20 }}
          />
        ) : <Empty description="Task not found" style={{ padding: 60 }} />
      )}

      {task && error && (
        <Alert
          banner
          showIcon
          type="warning"
          message="The latest update could not be loaded. Showing the last available task data."
          action={<Button size="small" onClick={refetch}>Retry</Button>}
          style={{ marginBottom: 8 }}
        />
      )}

      {task && (
        <Tabs
          className="workflow-task-tabs"
          tabBarStyle={{ background: token.colorBgElevated }}
          activeKey={activeTab}
          onChange={setActiveTab}
          size="small"
          style={{ marginTop: 0 }}
          items={tabs}
        />
      )}
      </div>
      </ScrollEdgeFrame>
    </Drawer>

    <SendToSuppliersModal
      queryPk={queryPk}
      task={task}
      open={sendModalOpen}
      userId={authState?.id}
      initialGroups={initialGroups}
      previouslyContactedIds={previouslyContactedIds}
      onClose={() => setSendModalOpen(false)}
      onSent={(supplierIds) => { onSent?.(queryPk, supplierIds); }}
    />

    <PriceUpdateDrawer
      card={priceDrawerCard}
      open={priceDrawerOpen}
      onClose={() => { setPriceDrawerOpen(false); refetch(); }}
      userId={authState?.id}
      authState={authState}
      userMap={userMap}
    />

    <UnavailablePartsModal
      open={unavailableModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      communicationChannel={communication?.channel || communication}
      unavailableParts={unavailableParts}
      userId={authState?.id}
      onClose={() => setUnavailableModalOpen(false)}
      onSuccess={refetch}
    />

    <BillingDetailsModal
      open={billingDetailsModalOpen}
      queryPk={queryPk}
      queryId={task?.query_id}
      customerName={task?.customer?.customer_real_name || task?.customer?.customer_name}
      customerPhone={task?.customer?.customer_mobile}
      communicationChannel={communication?.channel || communication}
      userId={authState?.id}
      onClose={() => setBillingDetailsModalOpen(false)}
      onSuccess={() => {
        setBillingDetailsModalOpen(false);
        onBillingDetailsSent?.(task?.customer?.customer_mobile);
      }}
    />
  </>
  );
}
